import React, { useEffect, useRef, useState } from 'react';
import { analytics } from '../../services/analytics';
import { ADS_ENABLED } from '../../config/ads';
import { NativePromo, NativePromoSlot } from './NativePromo';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

type AdSize = 'responsive' | 'leaderboard' | 'rectangle';

type AdSlot = NativePromoSlot;

type AdBannerProps = {
  /** Logical placement id for AdSense dashboard (maps to data-ad-slot via env). */
  slot: AdSlot;
  className?: string;
  /** Ad unit dimensions. "responsive" (default) auto-sizes; "leaderboard" = 728x90; "rectangle" = 300x250. */
  size?: AdSize;
  /** Visual style for the native-promo fallback shown when AdSense can't or shouldn't render. */
  fallbackVariant?: 'card' | 'banner' | 'compact';
};

const sizeStyles: Record<AdSize, React.CSSProperties> = {
  responsive: { display: 'block', minHeight: 90 },
  leaderboard: { display: 'inline-block', width: 728, height: 90 },
  rectangle: { display: 'inline-block', width: 300, height: 250 },
};

const slotEnvKey: Record<AdSlot, string | undefined> = {
  'home-mid': process.env.REACT_APP_ADSENSE_SLOT_HOME,
  'about-inline': process.env.REACT_APP_ADSENSE_SLOT_ABOUT,
  'try-interview-top': process.env.REACT_APP_ADSENSE_SLOT_TRY_TOP,
  'try-interview-bottom': process.env.REACT_APP_ADSENSE_SLOT_TRY_BOTTOM,
  'chat-bottom': process.env.REACT_APP_ADSENSE_SLOT_CHAT_BOTTOM,
  'footer-banner': process.env.REACT_APP_ADSENSE_SLOT_FOOTER,
  'sidebar-rectangle': process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR,
  'booking-history': process.env.REACT_APP_ADSENSE_SLOT_HISTORY,
};

// Shared responsive ad unit used for any placement without its own slot ID.
// Lets every placement serve real ads from a single AdSense ad unit; create
// dedicated units later and fill the per-slot env vars to split reporting.
const DEFAULT_SLOT = process.env.REACT_APP_ADSENSE_SLOT_DEFAULT;

const DISMISS_KEY = (slot: AdSlot) => `interviewr.ads.dismissed.${slot}`;
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h
// 6s is generous on purpose: AdSense often takes 3-4s on cold loads, and a
// brand-new account may serve later than that. Better to show an empty frame
// for a moment than to wrongly swap to NativePromo when AdSense was about to fill.
const NO_FILL_TIMEOUT_MS = 6000;

function isDismissed(slot: AdSlot): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY(slot));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Display AdSense unit with a friendly frame, consent gating, and a native-promo fallback.
 *
 * Behavior:
 * - If the user hasn't granted analytics/cookie consent, no third-party ad loads; we show a
 *   first-party promotional card instead so the slot stays useful.
 * - If the slot's AdSense ID isn't configured (or no fill within ~2.5s), fall back to NativePromo.
 * - Always renders a small "Sponsored" / "Promoted" label and a dismiss button (24h cooldown).
 */
export const AdBanner: React.FC<AdBannerProps> = ({
  slot,
  className = '',
  size = 'responsive',
  fallbackVariant,
}) => {
  const insRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);
  const client = process.env.REACT_APP_ADSENSE_CLIENT;
  const slotId = slotEnvKey[slot] || DEFAULT_SLOT;
  const testMode = process.env.REACT_APP_ADSENSE_TEST_MODE === '1';

  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed(slot));
  const [hasConsent, setHasConsent] = useState<boolean>(() => analytics.hasConsent());
  const [noFill, setNoFill] = useState<boolean>(false);

  // Re-check consent on focus (the cookie banner sits at the bottom and may
  // be accepted while this component is already mounted).
  useEffect(() => {
    const recheck = () => setHasConsent(analytics.hasConsent());
    window.addEventListener('focus', recheck);
    window.addEventListener('storage', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      window.removeEventListener('storage', recheck);
    };
  }, []);

  const adsenseEnabled = Boolean(client && slotId && hasConsent && !dismissed);

  useEffect(() => {
    if (!adsenseEnabled || !insRef.current || pushedRef.current) return;
    const insEl = insRef.current as HTMLElement;
    if (insEl.getAttribute('data-adsbygoogle-status')) {
      pushedRef.current = true;
      return;
    }
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch {
      /* swallow — fallback path will render NativePromo */
    }

    // In test mode we don't swap to NativePromo on no-fill — the whole point
    // of test mode is to see what AdSense actually does for this slot/account.
    if (testMode) return;

    const checkFill = window.setTimeout(() => {
      const adStatus = insEl.getAttribute('data-ad-status');
      const status = insEl.getAttribute('data-adsbygoogle-status');
      if (adStatus === 'unfilled') {
        setNoFill(true);
      } else if (status !== 'done' && !insEl.firstChild) {
        setNoFill(true);
      }
    }, NO_FILL_TIMEOUT_MS);

    return () => window.clearTimeout(checkFill);
  }, [adsenseEnabled, testMode]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY(slot), String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  // Ads are globally disabled (see src/config/ads.ts) — render nothing anywhere.
  if (!ADS_ENABLED) {
    return null;
  }

  if (dismissed) {
    return null;
  }

  const placeholderMin =
    size === 'rectangle' ? 'min-h-[250px]' : size === 'leaderboard' ? 'min-h-[90px]' : 'min-h-[120px]';

  const resolvedFallbackVariant: 'card' | 'banner' | 'compact' =
    fallbackVariant ?? (size === 'rectangle' ? 'card' : 'banner');

  // Path 1: fall back to native promo when AdSense can't render.
  if (!client || !slotId || !hasConsent || noFill) {
    return (
      <div className={`relative ${placeholderMin} ${className}`}>
        <NativePromo slot={slot} variant={resolvedFallbackVariant} />
      </div>
    );
  }

  const isResponsive = size === 'responsive';

  return (
    <div
      className={`relative overflow-hidden rounded-stitch-xl border border-stitch-border bg-stitch-surface px-1 pb-1 pt-6 shadow-stitch-card ${placeholderMin} ${className}`}
      data-ad-slot={slot}
    >
      <span
        className="pointer-events-none absolute left-3 top-1.5 rounded-full bg-stitch-surface-2 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-stitch-muted"
        aria-hidden="true"
      >
        Sponsored
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-2 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-stitch-muted transition before:absolute before:-inset-2 before:content-[''] hover:bg-stitch-surface-2 hover:text-stitch-fg"
        aria-label="Dismiss this ad for 24 hours"
      >
        <span className="material-symbols-outlined text-base">close</span>
      </button>
      <div className={`overflow-hidden ${size !== 'responsive' ? 'flex justify-center' : ''}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={sizeStyles[size]}
          data-ad-client={client}
          data-ad-slot={slotId}
          {...(testMode ? { 'data-adtest': 'on' } : {})}
          {...(isResponsive ? { 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' } : {})}
        />
      </div>
    </div>
  );
};
